import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { apiErr } from "@/lib/api";

export function AuthShell({ title, subtitle, children }) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-background px-4 relative overflow-hidden">
            <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-primary/10 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-accent/10 blur-3xl pointer-events-none" />
            <Card className="w-full max-w-sm p-8 relative z-10 shadow-2xl">
                <div className="flex items-center gap-2.5 mb-6">
                    <div className="rounded-lg bg-[#f7efe8] flex items-center justify-center px-3 py-2">
                        <img src="/moringa-logo.webp" alt="Moringa Finance" className="h-7 w-auto object-contain" />
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Finance</div>
                </div>
                <h1 className="font-heading text-2xl font-bold">{title}</h1>
                {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
                <div className="mt-6">{children}</div>
            </Card>
        </div>
    );
}

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setErr(""); setLoading(true);
        try {
            await login(email, password);
            navigate("/");
        } catch (e) {
            setErr(apiErr(e));
        } finally { setLoading(false); }
    };

    return (
        <AuthShell title="Welcome back" subtitle="Sign in to your portfolio">
            <form onSubmit={submit} className="space-y-4">
                <div className="space-y-1.5">
                    <Label>Email</Label>
                    <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                        placeholder="you@example.com" data-testid="login-email-input" autoComplete="email" />
                </div>
                <div className="space-y-1.5">
                    <div className="flex justify-between items-center">
                        <Label>Password</Label>
                        <Link to="/forgot-password" className="text-xs text-primary hover:underline" data-testid="forgot-password-link">Forgot?</Link>
                    </div>
                    <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                        placeholder="••••••••" data-testid="login-password-input" autoComplete="current-password" />
                </div>
                {err && <p className="text-sm text-loss" data-testid="login-error">{err}</p>}
                <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
                    {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Sign in
                </Button>
            </form>
        </AuthShell>
    );
}
